import React, { useState } from 'react';
import { FaPhoneAlt, FaUserAlt, FaBriefcase, FaCommentAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import TextCarousel from '../components/utilities/TextCarousel';
import { Link, useNavigate } from 'react-router-dom';
import AppointmentModal from '../components/utilities/AppointmentModal';
import { Helmet } from 'react-helmet';

const ContactPage: React.FC = () => {
    const cardVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: 'easeOut' } },
    };

    const items = [
        { text: ' 24x7 Helpline number - 1416 ', link: '' },
        { text: 'MANAS : 1-800 891 4416', link: '' },
        { text: 'Vandrevala : 919999666555', link: '' },
        { text: 'iCall : 9152987821', link: '' },
        { text: 'NIMHANS : +91 8046110007', link: '' },
        { text: 'Kiran : 1800-599-0019', link: '' },
        { text: 'AASRA : :9820466726', link: '' }
    ]

    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);
    const navigate = useNavigate();


    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>Contact Greh Pravesh Therapy - Mental Health Support & Consultation</title>
               <meta name="description" content="Get compassionate mental health support at Greh Pravesh Therapy. 24x7 helpline available. Book your appointment now: 8591049845." />

                {/* Open Graph / Social Media Tags */}
                <meta property="og:title" content="Mental Health Support - Contact & Connect" />
                <meta
                    property="og:description"
                    content="Reach out for personalized mental health support. Workplace wellness, individual therapy, and professional collaboration opportunities. Your well-being is our priority."
                />
                <meta property="og:type" content="healthcare.service" />
                <meta property="og:url" content="https://grehpraveshtherapy.com/contact" />
                <meta property="og:image" content="/images/contact.jpg" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Connect with Greh Pravesh Therapy" />
                <meta
                    name="twitter:description"
                    content="Mental health support at your fingertips. Book consultations, explore wellness solutions, and find compassionate care."
                />
                <meta name="twitter:image" content="/images/contact.jpg" />

                {/* Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "MentalHealthService",
                        "name": "Greh Pravesh Therapy",
                        "description": "Comprehensive mental health support providing personalized therapy, workplace wellness, and professional care",
                        "url": "https://grehpraveshtherapy.com/contact",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "telephone": "+911800599019",
                            "contactType": "Customer Support",
                            "availableLanguage": ["en", "hi"],
                            "email": "support@grehpraveshtherapy.com"
                        },
                        "areaServed": {
                            "@type": "GeoCircle",
                            "geoMidpoint": {
                                "@type": "GeoCoordinates",
                                "latitude": "YOUR_LATITUDE",
                                "longitude": "YOUR_LONGITUDE"
                            },
                            "geoRadius": "100 km"
                        },
                        "offers": {
                            "@type": "Offer",
                            "description": "Mental health consultation and support services",
                            "priceCurrency": "INR",
                            "price": "Varies"
                        },
                        "image": "/images/contact.jpg",
                        "keywords": [
                            "mental health support",
                            "therapy consultation",
                            "workplace wellness",
                            "mental health helpline"
                        ]
                    })}
                </script>

                {/* Additional Keywords */}
                <meta
                    name="keywords"
                    content="mental health support, therapy consultation, workplace wellness, psychological help, counseling services, 24x7 helpline, mental well-being"
                />
            </Helmet>
            <div
                className="text-[#333333] mt-16 min-h-screen bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: "url('/images/bg.png')" }}
            >
                {/* Landing Section */}
                <motion.section
                    className="mb-12 flex justify-center"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <div className="relative mt-12 md:mt-24 shadow-md rounded-[40px] w-[90%] md:w-full max-w-[1200px] overflow-hidden">
                        <img
                            src="/images/contact.jpg"
                            alt="Hero"
                            className="w-full h-[400px] sm:h-[450px] md:h-[500px] lg:h-[600px] object-cover"
                        />

                        <motion.div
                            className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-end items-center text-center px-4 sm:px-8 md:px-12 lg:px-16 pb-12 sm:pb-16 md:pb-20 lg:pb-24"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            <motion.h1
                                className="font-gentium-book-plus-regular text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-24 sm:mb-3 md:mb-4 text-yellow-300"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.6, duration: 0.6 }}
                            >
                                Your Mental Well-being,<br />
                                <span className="font-bold ">Our Responsibility</span>
                            </motion.h1>
                            <motion.p
                                className="text-sm sm:text-base md:text-lg text-white mb-4 sm:mb-5 md:mb-6 max-w-xl"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.8, duration: 0.6 }}
                            >
                                Contact Us for any concerns and for feedback
                            </motion.p>
                            <motion.button
                                className="bg-white text-[#333333] font-semibold px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full flex items-center text-sm sm:text-base transition-colors hover:bg-[#333333] hover:text-white"
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 1, duration: 0.6 }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={()=> navigate('/book-appointment')}
                            >
                                <img src="/images/avatar.jpg" alt="Avatar" className="w-8 h-8 rounded-full mr-2" />
                                Book a Consultation Session
                            </motion.button>
                        </motion.div>
                    </div>
                </motion.section>

                {/* Contact Intent Cards */}
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-4 gap-8 my-16 px-4 md:px-8"
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {[
                        {
                            icon: FaUserAlt,
                            title: "Find Your Healing Path",
                            description: "Navigating therapy can feel overwhelming. We provide a compassionate, judgment-free space to help you find the right support.",
                            buttonText: "Connect with a Therapist"
                        },
                        {
                            icon: FaBriefcase,
                            title: "Workplace Wellness",
                            description: "Empower your team with holistic mental health support that nurtures creativity, resilience, and well-being.",
                            buttonText: "Explore Workplace Solutions"
                        },
                        {
                            icon: FaCommentAlt,
                            title: "Join Our Care Community",
                            description: "We're building a diverse network of empathetic mental health professionals. Your unique perspective can make a difference.",
                            buttonText: "Become a Therapist"
                        },
                        {
                            icon: FaPhoneAlt,
                            title: "Let's Connect",
                            description: "Have questions about collaborations, partnerships, or career opportunities? We're here to listen and support.",
                            buttonText: "Reach Out"
                        }
                    ].map((card, index) => (
                        <motion.div
                            key={index}
                            className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center border border-[#8E44AD]"
                            variants={cardVariants}
                        >
                            <div className="bg-[#F39C12] rounded-full p-4 mb-4">
                                <card.icon className="text-4xl text-white" />
                            </div>
                            <h3 className="text-xl font-gentium-book-plus-regular  font-bold mb-2 text-[#2C3E50]">{card.title}</h3>
                            <p className="text-[#2C3E50] text-center mb-4">{card.description}</p>
                            <Link to="#" className="mt-4 bg-purple text-white rounded-full px-6 py-2 hover:bg-opacity-80 transition-colors">
                                {card.buttonText}
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>

                <div className="flex items-center justify-center  justify-center py-8 md:py-12">
                    <div className="container mx-auto px-4">
                        <TextCarousel items={items} speed={60} />
                    </div>
                </div>

                <AppointmentModal isOpen={isModalOpen} onClose={closeModal} />

            </div>
        </>
    );
};

export default ContactPage;